import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Feature flags per tenant (PARTE A del cantiere).
 *
 * Fornisce l'API `useFeatures()` che espone:
 *   - features: { presenze, ticket, messaggi, documenti, formazione,
 *                 onboarding, checklist, scadenze, verifica }: bool
 *   - modules: string[] (ordine canonico dei moduli)
 *   - loading: bool
 *   - refresh(): rilegge dal backend (utile dopo che il super admin cambia le flag)
 *
 * Convenzioni:
 *   - Se `user` è null → tutti i moduli OFF (nessuna sidebar → problema del layer di auth).
 *   - Loading iniziale: mantiene i flag a null → i consumer devono fallback a "nascondi"
 *     durante il primo fetch, per evitare flash di voci non autorizzate.
 */
const FeaturesContext = createContext(null);

const EMPTY = {
  presenze: false, ticket: false, messaggi: false, documenti: false,
  formazione: false, onboarding: false, checklist: false, scadenze: false,
  verifica: false, energia: false,
};

export const FeaturesProvider = ({ children }) => {
  const { user } = useAuth();
  const [features, setFeatures] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setFeatures(null);
      setModules([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/me/features");
      setFeatures(data.features || EMPTY);
      setModules(data.modules || []);
    } catch (e) {
      // In caso di errore rete/API, nascondi tutti i moduli per default sicuro.
      setFeatures(EMPTY);
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  return (
    <FeaturesContext.Provider value={{ features, modules, loading, refresh: load }}>
      {children}
    </FeaturesContext.Provider>
  );
};

export const useFeatures = () => {
  const ctx = useContext(FeaturesContext);
  if (!ctx) throw new Error("useFeatures deve essere usato dentro <FeaturesProvider>");
  return ctx;
};

/**
 * Helper: booleano semplice per un modulo. Torna false durante loading
 * (safe default: nasconde la voce finché non sappiamo per certo).
 */
export const useFeatureEnabled = (moduleName) => {
  const { features } = useFeatures();
  return !!(features && features[moduleName]);
};
